import { useState, useEffect } from "react";
import { Layout } from "@/components/Layout";
import { supabase } from "@/integrations/supabase/client";
import { ShieldCheck, BookOpen, Activity, Lock, Eye, EyeOff } from "lucide-react";

const CORRECT_PIN = "TWOJAstara17";
const PIN_SESSION_KEY = "egrob_admin_verified";

interface AdminLogEntry {
  id: string;
  action: string;
  module: string;
  user_email: string | null;
  created_at: string;
  details: string | null;
}

interface DocsPage {
  id: string;
  title: string;
  content: string;
  category: string;
  updated_at: string;
}

function PinGate({ onSuccess }: { onSuccess: () => void }) {
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [showPin, setShowPin] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (pin === CORRECT_PIN) {
      sessionStorage.setItem(PIN_SESSION_KEY, "1");
      onSuccess();
    } else {
      setError("Nieprawidłowe hasło. Spróbuj ponownie.");
      setPin("");
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--theme-bg-gradient)" }}>
      <div
        className="w-full max-w-sm mx-4 p-8 rounded-2xl"
        style={{
          background: "rgba(12, 14, 30, 0.95)",
          border: "1px solid rgba(212, 175, 55, 0.2)",
          boxShadow: "0 24px 60px rgba(0,0,0,0.6)",
        }}
      >
        <div className="flex flex-col items-center mb-8">
          <div
            className="w-14 h-14 rounded-xl flex items-center justify-center mb-4"
            style={{ background: "rgba(212, 175, 55, 0.1)", border: "1px solid rgba(212, 175, 55, 0.2)" }}
          >
            <ShieldCheck className="w-7 h-7" style={{ color: "#d4af37" }} />
          </div>
          <h1 className="text-xl font-bold text-white">Panel Administratora</h1>
          <p className="text-sm mt-1" style={{ color: "rgba(255,255,255,0.4)" }}>
            Wprowadź hasło dostępu
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="relative">
            <input
              type={showPin ? "text" : "password"}
              value={pin}
              onChange={(e) => { setPin(e.target.value); setError(""); }}
              placeholder="Hasło dostępu"
              autoComplete="current-password"
              className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-all duration-200 pr-12"
              style={{
                background: "rgba(255,255,255,0.05)",
                border: error ? "1px solid rgba(239,68,68,0.5)" : "1px solid rgba(255,255,255,0.1)",
                color: "white",
              }}
            />
            <button
              type="button"
              onClick={() => setShowPin(v => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2"
              style={{ color: "rgba(255,255,255,0.3)" }}
            >
              {showPin ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>

          {error && (
            <p className="text-xs text-red-400">{error}</p>
          )}

          <button
            type="submit"
            className="w-full py-3 rounded-xl text-sm font-semibold transition-all duration-200 hover:brightness-110 active:scale-95"
            style={{ background: "#d4af37", color: "rgba(8,9,18,0.95)" }}
          >
            Zaloguj do panelu
          </button>
        </form>
      </div>
    </div>
  );
}

function AdminDashboard() {
  const [tab, setTab] = useState<"docs" | "log">("docs");
  const [logs, setLogs] = useState<AdminLogEntry[]>([]);
  const [docs, setDocs] = useState<DocsPage[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      setLoading(true);
      const [logsRes, docsRes] = await Promise.all([
        supabase.from("admin_log").select("*").order("created_at", { ascending: false }).limit(50),
        supabase.from("docs_pages").select("*").order("category, title"),
      ]);
      if (logsRes.data) setLogs(logsRes.data as AdminLogEntry[]);
      if (docsRes.data) setDocs(docsRes.data as DocsPage[]);
      setLoading(false);
    }
    load();
  }, []);

  const tabStyle = (active: boolean) => ({
    background: active ? "rgba(212,175,55,0.12)" : "transparent",
    border: `1px solid ${active ? "rgba(212,175,55,0.3)" : "rgba(255,255,255,0.06)"}`,
    color: active ? "#d4af37" : "rgba(255,255,255,0.5)",
  });

  return (
    <div className="min-h-screen" style={{ background: "var(--theme-bg-gradient)" }}>
      <div className="max-w-5xl mx-auto px-4 py-20">
        <div className="flex items-center gap-3 mb-8">
          <ShieldCheck className="w-7 h-7" style={{ color: "#d4af37" }} />
          <h1 className="text-2xl font-bold text-white">Panel Administratora</h1>
          <span className="ml-auto text-xs px-2 py-1 rounded-full" style={{ background: "rgba(212,175,55,0.1)", color: "#d4af37", border: "1px solid rgba(212,175,55,0.2)" }}>
            e-grób v0.17.0-alpha
          </span>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setTab("docs")}
            className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all"
            style={tabStyle(tab === "docs")}
          >
            <BookOpen className="w-4 h-4" /> Dokumentacja
          </button>
          <button
            onClick={() => setTab("log")}
            className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all"
            style={tabStyle(tab === "log")}
          >
            <Activity className="w-4 h-4" /> Dziennik zdarzeń
          </button>
        </div>

        {loading ? (
          <div className="text-center py-12" style={{ color: "rgba(255,255,255,0.3)" }}>Ładowanie...</div>
        ) : tab === "docs" ? (
          <div className="space-y-4">
            {docs.length === 0 ? (
              <div className="rounded-xl p-8 text-center" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}>
                <p style={{ color: "rgba(255,255,255,0.3)" }}>Brak dokumentacji w bazie.</p>
              </div>
            ) : (
              docs.map(doc => (
                <div key={doc.id} className="rounded-xl p-5" style={{ background: "rgba(12,14,30,0.7)", border: "1px solid rgba(255,255,255,0.06)" }}>
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-semibold text-white">{doc.title}</h3>
                    <span className="text-xs px-2 py-0.5 rounded-full ml-3 flex-shrink-0" style={{ background: "rgba(212,175,55,0.1)", color: "#d4af37", border: "1px solid rgba(212,175,55,0.2)" }}>
                      {doc.category}
                    </span>
                  </div>
                  <p className="text-sm whitespace-pre-wrap" style={{ color: "rgba(255,255,255,0.55)" }}>{doc.content}</p>
                  <p className="text-xs mt-3" style={{ color: "rgba(255,255,255,0.2)" }}>
                    Zaktualizowano: {new Date(doc.updated_at).toLocaleString("pl-PL")}
                  </p>
                </div>
              ))
            )}
          </div>
        ) : (
          <div className="space-y-2">
            {logs.length === 0 ? (
              <div className="rounded-xl p-8 text-center" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}>
                <p style={{ color: "rgba(255,255,255,0.3)" }}>Brak wpisów w dzienniku.</p>
              </div>
            ) : (
              logs.map(log => (
                <div key={log.id} className="flex items-start gap-3 rounded-xl p-4" style={{ background: "rgba(12,14,30,0.7)", border: "1px solid rgba(255,255,255,0.05)" }}>
                  <div className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0" style={{ background: "#d4af37" }} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-white">{log.action}</span>
                      <span className="text-xs px-1.5 py-0.5 rounded" style={{ background: "rgba(255,255,255,0.06)", color: "rgba(255,255,255,0.4)" }}>
                        {log.module}
                      </span>
                      {log.user_email && (
                        <span className="text-xs" style={{ color: "rgba(212,175,55,0.6)" }}>{log.user_email}</span>
                      )}
                    </div>
                    {log.details && (
                      <p className="text-xs mt-1" style={{ color: "rgba(255,255,255,0.35)" }}>{log.details}</p>
                    )}
                    <p className="text-xs mt-1" style={{ color: "rgba(255,255,255,0.2)" }}>
                      {new Date(log.created_at).toLocaleString("pl-PL")}
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function AdminPage() {
  const [verified, setVerified] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem(PIN_SESSION_KEY) === "1") {
      setVerified(true);
    }
  }, []);

  if (!verified) {
    return <PinGate onSuccess={() => setVerified(true)} />;
  }

  return <Layout><AdminDashboard /></Layout>;
}
